import React from 'react';
import type { HeaderState } from '../../reducers/header-reducer';
import AudiHeaderDealerContext from '../AudiHeaderDealerContext/AudiHeaderDealerContext';
import DealerSearch from '../AudiHeaderDealerContext/DealerSearch';
import AudiHeaderDealerContextAlpha from '../AudiHeaderDealerContext/AudiHeaderDealerContext_alpha';
import { DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE } from '../../utils/globals';
import useGeolocation from '../../hooks/use-geolocation';

interface DealerComponentsProps {
  headerState: HeaderState;
  showDealerContext: boolean;
  headerNavigationItemsAmount: number;
  dealerSearch: boolean;
  isTierOneDomain: boolean;
  dealerSearchGeolocationEnabled: boolean;
  onClickDealerContext: () => void;
  onClickDealerSearchContext: () => void;
  isSmallView: boolean;
}

const DealerComponents: React.FC<DealerComponentsProps> = ({
  headerState,
  showDealerContext,
  headerNavigationItemsAmount,
  dealerSearch,
  isTierOneDomain,
  dealerSearchGeolocationEnabled,
  onClickDealerContext,
  onClickDealerSearchContext,
  isSmallView,
}) => {
  const isLegacy = Boolean(process.env.IS_LEGACY);
  const isDealerContextLegacy = showDealerContext && isTierOneDomain && isLegacy;
  const isDealerContextAlpha = showDealerContext && isTierOneDomain && !isLegacy;
  const isDealerSearchLegacyDesktop = dealerSearch && isTierOneDomain && isLegacy && !isSmallView;
  const isDealerSearchAlphaDesktop = dealerSearch && isTierOneDomain && !isLegacy && !isSmallView;
  const autoConnectedUnsubscribed =
    typeof window !== 'undefined' &&
    Boolean(sessionStorage.getItem(DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE));

  const { geoError } = useGeolocation(
    dealerSearch,
    dealerSearchGeolocationEnabled,
    autoConnectedUnsubscribed,
    isTierOneDomain,
  );

  // disable 'dealerSearchGeolocationEnabled' is user had already disconnected by checking sessionStorage DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE
  const gelocationEnabled =
    dealerSearchGeolocationEnabled && !autoConnectedUnsubscribed && !geoError;

  // Legacy DealerContext Desktop View
  if (isDealerContextLegacy) {
    return (
      <AudiHeaderDealerContext
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        dealerSearch={dealerSearch}
        onClickDealerContext={onClickDealerContext}
        onClickDealerSearchContext={onClickDealerSearchContext}
        showDealerAdditionalData={headerState.activeItem.showDealer}
        showDealerSearch={headerState.activeItem.showDealerSearch}
      />
    );
  }

  // Legacy DealerSearch Desktop View
  if (isDealerSearchLegacyDesktop) {
    return (
      <DealerSearch
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        onClickDealerSearchContext={onClickDealerSearchContext}
        dealerSearchGeolocationEnabled={gelocationEnabled}
        showDealerSearch={gelocationEnabled ? true : headerState.activeItem.showDealerSearch}
      />
    );
  }

  // Alpha DealerContext Desktop View
  if (isDealerContextAlpha) {
    return (
      <AudiHeaderDealerContextAlpha
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        dealerSearch={dealerSearch}
        onClickDealerContext={onClickDealerContext}
        onClickDealerSearchContext={onClickDealerSearchContext}
        showDealerAdditionalData={headerState.activeItem.showDealer}
        isMobileView={isSmallView}
        showDealerSearch={headerState.activeItem.showDealerSearch}
      />
    );
  }

  // Alpha DealerSearch Desktop View
  if (isDealerSearchAlphaDesktop) {
    return (
      <DealerSearch
        headerNavigationItemsAmount={headerNavigationItemsAmount}
        onClickDealerSearchContext={onClickDealerSearchContext}
        dealerSearchGeolocationEnabled={gelocationEnabled}
        showDealerSearch={gelocationEnabled ? true : headerState.activeItem.showDealerSearch}
      />
    );
  }

  return null;
};

export default DealerComponents;
